/**
 * useCrmWorkspacesHierarchy - Hook para buscar hierarquia de Workspaces e Pipelines
 * 
 * Retorna a estrutura aninhada:
 * - Workspaces (com ícone, cor)
 *   - Pipelines (nome, is_default)
 * 
 * Fases NÃO são incluídas - são carregadas no Kanban como colunas.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Interface para um Pipeline dentro de um Workspace
 */
export interface PipelineItem {
  id: string;
  name: string;
  is_default: boolean;
  sort_order: number;
}

/**
 * Interface para a hierarquia de Workspace com Pipelines
 */
export interface WorkspaceHierarchy {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  pipelines: PipelineItem[];
}

/**
 * Hook para buscar workspaces com seus pipelines aninhados
 * 
 * @returns Objeto com dados da hierarquia, estado de loading e erro
 */
export function useCrmWorkspacesHierarchy() {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['crm-workspaces-hierarchy', currentTenant?.id],
    queryFn: async (): Promise<WorkspaceHierarchy[]> => {
      if (!currentTenant?.id) return [];

      // Busca server-side: RPC retorna workspaces ativos com pipelines aninhados (JSONB).
      // Substitui SELECTs diretos a crm_workspaces e crm_pipelines (v1.0.0 da RPC).
      const { data, error } = await supabase.rpc('get_crm_workspaces_hierarchy', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;

      // Mapeia o resultado da RPC para o shape público do hook
      const hierarchy: WorkspaceHierarchy[] = (data || []).map((row: any) => ({
        id: row.workspace_id,
        name: row.workspace_name,
        icon: row.workspace_icon,
        color: row.workspace_color,
        sort_order: row.workspace_sort_order,
        pipelines: (Array.isArray(row.pipelines) ? row.pipelines : []).map((p: any) => ({
          id: p.id,
          name: p.name,
          is_default: !!p.is_default,
          sort_order: p.sort_order,
        })),
      }));

      return hierarchy;
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  return {
    workspaces: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch
  };
}
